/**
 * Tracing helper para flujos críticos (Sentry performance).
 *
 * Wraps async operations en spans con nombre + tags estándar (tenant_id, user_id, ok).
 * No-op si Sentry no está configurado (DSN ausente).
 *
 * Convención de nombres: `<modulo>.<operacion>`
 *   - wa.send                 → envío de mensaje saliente
 *   - wa.thread.load          → carga inicial de mensajes de una conversación
 *   - wa.thread.older         → paginación hacia atrás
 *   - wa.assign               → asignación de agente/equipo
 *   - wa.macro.run            → ejecución de macro
 *   - wa.conversations.load   → fetch de lista de conversaciones
 *
 * Uso:
 *   const result = await traceSpan('wa.send', { tenant_id, user_id }, async () => {
 *     return await supabase.functions.invoke('whatsapp-send-message', {...});
 *   });
 */

import { conSpan } from '@/lib/sentryPuente';

export interface TraceAttributes {
  tenant_id?: string;
  user_id?: string;
  conversation_id?: string;
  /** Atributos extra (se serializan como tags). Mantener short keys, primitive values. */
  [key: string]: string | number | boolean | undefined;
}

/**
 * Ejecuta una función async dentro de un span de Sentry.
 * El span captura: duración, tags pasados, status (ok/error), exception si throw.
 * Si Sentry no está inicializado el callback corre directo sin overhead.
 */
export async function traceSpan<T>(
  name: string,
  attributes: TraceAttributes,
  fn: () => Promise<T>,
): Promise<T> {
  return conSpan(
    name,
    name.split('.')[0] ?? 'app',
    cleanAttrs(attributes),
    async (span) => {
      try {
        const result = await fn();
        span?.setStatus({ code: 1 }); // ok
        return result;
      } catch (err) {
        span?.setStatus({ code: 2, message: 'internal_error' }); // error
        throw err;
      }
    },
  );
}

/** Versión sync. Usar solo cuando la operación medida es síncrona. */
export function traceSpanSync<T>(
  name: string,
  attributes: TraceAttributes,
  fn: () => T,
): T {
  return conSpan(
    name,
    name.split('.')[0] ?? 'app',
    cleanAttrs(attributes),
    (span) => {
      try {
        const result = fn();
        span?.setStatus({ code: 1 });
        return result;
      } catch (err) {
        span?.setStatus({ code: 2, message: 'internal_error' });
        throw err;
      }
    },
  );
}

function cleanAttrs(attrs: TraceAttributes): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(attrs)) {
    if (v !== undefined && v !== null) out[k] = v;
  }
  return out;
}
